import { Grid, Link, Typography } from "@mui/material";
import React, { FunctionComponent, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { EvaluationStatus } from "../../../../models/Evaluation";
import {
    EvalActionNotificationDetails,
    EvaluationActionNotificationArg,
} from "../../../../stores/Notifications/Models/EvalActionNotification";
import hexToRGB from "../../../../utils/hexToRGB";
import AcxInputLabel from "../../../UI/AcxInputLabel";

const NoteTruncateLength = 233;


function ReviewCompleteDetails(props: {
    actionNotification: EvaluationActionNotificationArg;
    reviewDetails: EvalActionNotificationDetails[];
}) {
    const reviewDetails = useMemo(
        () => props.reviewDetails.slice(0, 4),
        [props.reviewDetails],
    );

    return (
        <>
            {reviewDetails.map((value) => {
                return (
                    <Grid
                        key={`${value.fromUserName}-${value.notesLength}`}
                        item
                        xs={12}
                        sx={{
                            marginTop: 1,
                        }}
                    >
                        <AcxInputLabel
                            sx={{
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                fontSize: "13px",
                                fontWeight: "bold",
                                letterSpacing: "0",
                                lineHeight: "20px",
                                width: "100%",
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            labelText={
                                reviewDetails.length === 1
                                    ? `Response`
                                    : `${value.fromUserName} Response`
                            }
                        />
                        <div
                            style={{
                                paddingLeft: 4.8,
                                paddingRight: 2,
                                paddingTop: 5.6,
                                paddingBottom: 5.6,
                                backgroundColor: hexToRGB("#888888", 0.12),
                                borderRadius: "0 10px 16px 5px",
                            }}
                        >
                            <Typography
                                sx={{
                                    wordWrap: "break-word",
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "break-word",
                                    fontSize: "13px",
                                    fontWeight: "bold",
                                    letterSpacing: "0",
                                    lineHeight: "20px",
                                    width: "100%",
                                }}
                                color={"textPrimary"}
                            >
                                {value.actionNotes?.substr(
                                    0,
                                    NoteTruncateLength,
                                )}{" "}
                                {value.notesLength > NoteTruncateLength ? (
                                    <>...</>
                                ) : (
                                    <></>
                                )}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "text.secondary",
                                    marginTop: 0.35,
                                    wordWrap: "break-word",
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "break-word",
                                    width: "100%",
                                }}
                            >
                                {value.createdOn}
                            </Typography>
                        </div>
                    </Grid>
                );
            })}
        </>
    );
}
interface OwnProps {
    actionNotification: EvaluationActionNotificationArg;
    appPath?: string;
    status: EvaluationStatus;
}

type Props = OwnProps;
const EvalActionNotification: FunctionComponent<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = (props) => {
    if (
        props.actionNotification.highestPrecedenceActionType === "RequestReview"
    ) {
        const { notes, notesLength } =
            props.actionNotification.getNotesForRequestReview();
        return (
            <Grid container>
                <Grid item xs={12} sx={{}}>
                    <AcxInputLabel labelText={"Requested By"} />
                    <Typography
                        component={"div"}
                        variant={"subtitle2"}
                        color={"textPrimary"}
                    >
                        {props.actionNotification.getFromUserForRequestReview()}{" "}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: 1 }}>
                    <AcxInputLabel labelText={"Interaction"} />
                    <Link className="pendo-ignore"
                        key={props.actionNotification.interactionName}
                        variant="body1"
                        component={props.appPath ? RouterLink : "button"}
                        to={`${props.appPath}`}
                        color="textPrimary"
                        sx={{
                            textAlign: "left",
                            color: "#3564D5",
                            fontWeight: "bold",
                            pointerEvents: !props.appPath ? "none" : "auto",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#3564D5",
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                width: "100%",
                            }}
                        >
                            {props.actionNotification.interactionName}
                        </Typography>
                    </Link>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: 1 }}>
                    <AcxInputLabel labelText={"Reason"} />
                    <Typography color={"textPrimary"}>
                        {props.actionNotification.getReasonForRequestReview()}{" "}
                        Module
                    </Typography>

                    <AcxInputLabel
                        sx={{ marginTop: 1, marginBottom: 1 }}
                        labelText={"Notes"}
                    />
                    <div
                        style={{
                            paddingLeft: 4.8,
                            paddingRight: 2,
                            paddingTop: 5.6,
                            paddingBottom: 5.6,
                            backgroundColor: hexToRGB("#888888", 0.12),
                            borderRadius: "0 10px 16px 5px",
                        }}
                    >
                        <Typography
                            sx={{
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                fontSize: "13px",
                                fontWeight: "bold",
                                letterSpacing: "0",
                                lineHeight: "20px",
                                width: "100%",
                            }}
                            color={"textPrimary"}
                        >
                            {notes?.substr(0, NoteTruncateLength)}{" "}
                            {notesLength > NoteTruncateLength ? (
                                <>...</>
                            ) : (
                                <></>
                            )}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "12px",
                                color: "text.secondary",
                                marginTop: 0.35,
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                width: "100%",
                            }}
                        >
                            {props.actionNotification.getCreatedOnForRequestReview()}{" "}
                        </Typography>
                    </div>
                </Grid>
            </Grid>
        );
    } else if (
        props.actionNotification.highestPrecedenceActionType ===
        "CompleteReview"
    ) {
        return (
            <Grid container>
                <Grid item xs={12} sx={{}}>
                    <AcxInputLabel
                        labelText={
                            props.actionNotification
                                .completedReviewActionsCnt === 1
                                ? `New Review Completed By`
                                : `${props.actionNotification.completedReviewActionsCnt} New Reviews Completed By`
                        }
                    />
                    <Typography
                        component={"div"}
                        variant={"subtitle2"}
                        color={"textPrimary"}
                    >
                        {props.actionNotification
                            .getFromUserForCompleteReview()
                            .join(", ")}{" "}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: 1 }}>
                    <AcxInputLabel labelText={"Interaction"} />
                    <Link className="pendo-ignore"
                        key={props.actionNotification.interactionName}
                        variant="body1"
                        component={props.appPath ? RouterLink : "button"}
                        to={`${props.appPath}`}
                        color="textPrimary"
                        sx={{
                            textAlign: "left",
                            color: "#3564D5",
                            fontWeight: "bold",
                            pointerEvents: !props.appPath ? "none" : "auto",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#3564D5",
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                width: "100%",
                            }}
                        >
                            {props.actionNotification.interactionName}
                        </Typography>
                    </Link>
                </Grid>
                <ReviewCompleteDetails
                    actionNotification={props.actionNotification}
                    reviewDetails={props.actionNotification.details.filter(
                        (value) => value.actionType === "CompleteReview",
                    )}
                />
            </Grid>
        );
    } else if (props.actionNotification.reviewCommentActionsCnt > 0) {
        return <></>;
    } else {
        return <></>;
    }
};

export default EvalActionNotification;
